import React from 'react';

const DataTable = ({
  title,
  data,
  columns,
  loading,
  searchTerm,
  onSearchChange,
  entriesPerPage,
  onEntriesPerPageChange,
  currentPage,
  onPageChange,
  showFilters = true,
  onFilterClick,   // 👈 NEW
  showExport = true,
  showCustomizeColumns = true,
  emptyMessage = "No data found",
  className = ""
}) => {
  // Filter data based on search term
  const filteredData = data.filter(item => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return columns.some(column => {
      const value = item[column.key];
      return value && value.toString().toLowerCase().includes(searchLower);
    });
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const renderCell = (item, column) => {
    if (column.render) {
      return column.render(item[column.key], item);
    }
    return item[column.key];
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxButtons = 7;

    // Previous button
    buttons.push(
      <button
        key="prev"
        className="pagination-btn"
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      >
        <i className="fas fa-chevron-left"></i>
      </button>
    );

    // Page number buttons
    let startPage, endPage;
    if (totalPages <= maxButtons) {
      startPage = 1;
      endPage = totalPages;
    } else if (currentPage <= 4) {
      startPage = 1;
      endPage = maxButtons;
    } else if (currentPage >= totalPages - 3) {
      startPage = totalPages - maxButtons + 1;
      endPage = totalPages;
    } else {
      startPage = currentPage - 3;
      endPage = currentPage + 3;
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        className="pagination-btn"
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      >
        <i className="fas fa-chevron-right"></i>
      </button>
    );

    return buttons;
  };

  return (
    <div className={`data-table-section ${className}`}>
      {title && (
        <div className="section-header">
          <h3>{title}</h3>
          <div className="section-actions">
            {showExport && (
              <button className="btn-export">
                <i className="fas fa-upload"></i> Export
              </button>
            )}
            {showCustomizeColumns && (
              <button className="btn-customize">
                Customize Columns
              </button>
            )}
          </div>
        </div>
      )}

      <div className="table-controls">
        <div className="control-left">
          <label>
            Show
            <select
              value={entriesPerPage}
              onChange={(e) => onEntriesPerPageChange(Number(e.target.value))}
              className="entries-select"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            entries
          </label>
        </div>

        <div className="control-right">
          {showFilters && (
            <button className="btn-filters" onClick={onFilterClick}>
              <i className="fas fa-filter"></i> Filters
            </button>
          )}
          <div className="search-control">
            <label>Search:</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search..."
              className="search-control-input"
            />
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={column.className || ''}>
                  {column.key === 'expand' && <i className="fas fa-plus expand-icon"></i>}
                  {column.title}
                  {column.sortable && <i className="fas fa-arrow-up sort-icon"></i>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="loading-cell">
                  <i className="fas fa-spinner fa-spin"></i> Loading...
                </td>
              </tr>
            ) : currentData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="empty-cell">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              currentData.map((item, index) => (
                <tr key={item.id || index}>
                  {columns.map((column) => (
                    <td key={column.key} className={column.cellClassName || ''}>
                      {renderCell(item, column)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div className="pagination-info">
          Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} entries
        </div>
        <div className="pagination">
          {renderPaginationButtons()}
        </div>
      </div>
    </div>
  );
};

export default DataTable;